/**
 * TaskListView - Virtualized task list for up to 500 tasks.
 *
 * Uses react-window v2 List for efficient rendering of large task lists.
 * Renders TaskItem for each row. Shows EmptyState when no tasks are present.
 *
 * @see Requirements 3.2, 13.6
 */

import { useCallback, type CSSProperties, type ReactElement } from 'react';
import { List } from 'react-window';
import type { Task } from '@/types';
import { EmptyState } from '@/components/ui/EmptyState';
import { TaskItem } from './TaskItem';

export interface TaskListViewProps {
  /** Array of tasks to render. */
  tasks: Task[];
  /** Called when a task row is clicked (opens detail panel). */
  onTaskClick: (taskId: string) => void;
  /** Called when a task's completion checkbox is toggled. */
  onComplete: (taskId: string) => void;
  /** Called when the empty-state CTA is used to create the first task. */
  onCreateTask?: () => void;
}

/** Height of each task row in pixels. */
const ITEM_HEIGHT = 56;

/** Props passed to each row component by react-window + our custom rowProps. */
interface TaskRowProps {
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
  onComplete: (taskId: string) => void;
}

/** Row component for react-window v2. */
function TaskRow({
  index,
  style,
  tasks,
  onTaskClick,
  onComplete,
}: {
  ariaAttributes: {
    'aria-posinset': number;
    'aria-setsize': number;
    role: 'listitem';
  };
  index: number;
  style: CSSProperties;
} & TaskRowProps): ReactElement | null {
  const task = tasks[index];
  if (!task) return null;

  return (
    <TaskItem
      task={task}
      style={style}
      onClick={() => onTaskClick(task.taskId)}
      onComplete={() => onComplete(task.taskId)}
    />
  );
}

export function TaskListView({ tasks, onTaskClick, onComplete, onCreateTask }: TaskListViewProps) {
  const rowKey = useCallback(
    (index: number) => {
      return tasks[index]?.taskId ?? index;
    },
    [tasks]
  );

  if (tasks.length === 0) {
    return (
      <EmptyState
        title="No tasks yet"
        description="Create your first task to get started with organizing your day."
        action={
          onCreateTask
            ? { label: 'Create your first task', onClick: onCreateTask }
            : undefined
        }
        icon={
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="8"
              y="6"
              width="32"
              height="36"
              rx="4"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M16 18H32M16 26H28M16 34H24"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="37" cy="37" r="8" fill="var(--color-primary)" opacity="0.2" />
            <path
              d="M34 37H40M37 34V40"
              stroke="var(--color-primary)"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        }
      />
    );
  }

  return (
    <div
      className="flex-1 min-h-0 w-full"
      role="list"
      aria-label="Task list"
    >
      <List
        rowComponent={TaskRow}
        rowCount={tasks.length}
        rowHeight={ITEM_HEIGHT}
        rowKey={rowKey}
        rowProps={{ tasks, onTaskClick, onComplete }}
        overscanCount={5}
        defaultHeight={Math.min(tasks.length * ITEM_HEIGHT, 600)}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
}
